
import Vue, { PropType } from 'vue'
import Input from '@/components/Input.vue'

export default Vue.extend({
    name: 'Dropdown',
    components: { Input },
    props: {
        options: {
            type: Object as PropType<{ [id: string]: string }>,
            default: () => ({}), // options to display, default none, type is Object
        },
        value: {
            type: String,
            default: null, // selected option
        },
        errMsg: {
            type: String,
            default: '', // error msg when validate failed
        },

        required: {
            type: Boolean,
            default: false, // if field required
        },
        title: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hasTitle: {
            type: Boolean,
            default: true,
        },
        //Reset bit - flipping this will cause the element to reset hasFocused to reset
        resetBit: {
            type: Boolean,
            default: false,
        },
        vuelidater: {
            type: Object,
            default: () => ({ $invalid: false }),
        },
    },
    data() {
        return {
            hasFocused: false, // if has focused once
            focusing: false, // if currently focusing
            displayText: this.options[(this.value as any) || ''] || '', // text to display
            searching: false, // flag if typing to search
            highlighting: -1, // which option is highlighting when press keyboard
        }
    },

    computed: {
        optionsFiltered(): object {
            if (!this.displayText || !this.searching) {
                return this.options
            }

            const searchString = this.displayText.toLowerCase()
            const filteredOptions = Object.entries(this.options).filter(
                ([key, value]) => {
                    return value.toLowerCase().includes(searchString)
                }
            )

            if (filteredOptions.length === 0) {
                return { '': 'No result found' }
            }

            return Object.fromEntries(filteredOptions)
        },

        hasError(): boolean {
            return this.required && !this.value
        },

        optionsFilteredLength(): number {
            return this.optionsFilteredIds.length
        },

        optionsFilteredIds(): string[] {
            return Object.keys(this.optionsFiltered)
        },

        highlightingId(): string {
            return this.optionsFilteredIds[this.highlighting]
        },
    },

    watch: {
        value(newValue) {
            this.displayText = this.options[newValue] || ''
            this.$emit('change', newValue)
        },
        hasError: function (hasError) {
            this.$emit('has-error', hasError)
        },
        resetBit: function (resetBit) {
            this.hasFocused = false
        },
    },

    methods: {
        onInput() {
            if (this.disabled) {
                return
            }
            this.searching = true
            // when typing default to no hightlight if result too short
            this.highlighting =
                this.highlighting < this.optionsFilteredLength
                    ? this.highlighting
                    : -1
            // better focus it
            this.onFocus()
        },

        onFocusOut(e) {
            // also fires when select, so let that go first
            setTimeout(() => {
                this.focusing = false
                this.displayText = this.options[(this.value as any) || ''] || ''
            }, 200)
        },

        onFocus() {
            if (this.disabled) {
                return
            }
            this.focusing = true
            this.hasFocused = true
        },

        onClick() {
            if (this.disabled) {
                return
            }
            this.focusing = !this.focusing
            this.hasFocused = true
        },

        // select an option
        selectOption(id) {
            if (this.disabled) {
                return
            }
            this.highlighting = -1
            this.focusing = false
            this.$emit('input', id)
            this.searching = false
        },

        onKeyUp(e) {
            if (this.disabled) {
                return
            }
            if (e.code === 'ArrowUp') {
                if (this.highlighting > 0) {
                    this.highlighting--
                }
            } else if (e.code === 'ArrowDown') {
                if (this.highlighting < this.optionsFilteredLength - 1) {
                    this.highlighting++
                }
            } else if (e.code === 'Enter' && this.highlighting > -1) {
                this.selectOption(this.highlightingId)
            }
        },

        dropdownItemClass(display, id) {
            const index = this.indexOfId(id)
            return { 'item-hover': index > -1 && index === this.highlighting }
        },

        indexOfId(id) {
            return this.optionsFilteredIds.indexOf(`${id}`)
        },

        onHover(id) {
            if (this.disabled) {
                return
            }
            this.highlighting = this.indexOfId(id)
        },
    },
})
